import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, BellOff, CheckCircle2, FlaskConical, Repeat, UserPlus, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/app/PageHeader';
import { StatCard } from '@/components/app/StatCard';
import { TimeseriesChart } from '@/components/app/TimeseriesChart';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { humanizeError } from '@/lib/apiClient';
import {
  dashboardApi,
  type ExperimentHealth,
  type FunnelCounts,
  type PivotDashboard as PivotData,
} from './api/dashboardApi';
import { formatRelative } from './formatRelative';

const HEALTH: Record<ExperimentHealth, { label: string; variant: 'success' | 'warning' | 'destructive' | 'muted' }> = {
  on_track: { label: 'On track', variant: 'success' },
  slipping: { label: 'Slipping', variant: 'warning' },
  silent: { label: 'Silent', variant: 'destructive' },
  new: { label: 'New', variant: 'muted' },
};

const FUNNEL_STEPS: { key: keyof FunnelCounts; label: string; short: string }[] = [
  { key: 'signedUp', label: 'Signed up', short: 'Signed up' },
  { key: 'startedExperiment', label: 'Started an experiment', short: 'Started' },
  { key: 'loggedOnce', label: 'Logged a day', short: 'Logged' },
  { key: 'cameBack', label: 'Came back (logged a 2nd day)', short: 'Came back' },
  { key: 'logged4Days', label: 'Logged 4+ days in one experiment', short: '4+ days' },
  { key: 'secondExperiment', label: 'Started a 2nd experiment', short: '2nd exp' },
];

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

export function PivotDashboard() {
  const query = useQuery({
    queryKey: ['dashboard', 'pivot'],
    queryFn: () => dashboardApi.pivot(),
    staleTime: 60_000,
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data)
    return (
      <ErrorState
        title="Dashboard failed to load"
        description={humanizeError(query.error)}
        actionLabel="Retry"
        onAction={() => query.refetch()}
      />
    );

  const d = query.data;
  const k = d.kpis;
  const f = d.funnel;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Experiment loop"
        description={`Funnel counts users who signed up since the pivot (${formatDay(d.pivotStart)}). ${d.excludedUsers} internal accounts excluded. Days are UTC. Updated ${formatRelative(d.generatedAt)}.`}
      />

      <ReminderLine reminder={d.reminder} />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatCard label="Logged today" value={k.loggedToday} hint={`of ${k.liveExperiments} live`} icon={CheckCircle2} />
        <StatCard label="Logged · 7d" value={k.weeklyLoggers} hint="unique users" icon={Users} />
        <StatCard label="Live experiments" value={k.liveExperiments} icon={FlaskConical} />
        <StatCard
          label="Came back"
          value={`${pct(f.cameBack, f.loggedOnce)}%`}
          hint={`${f.cameBack} of ${f.loggedOnce} who logged day 1`}
          icon={Repeat}
          tone={f.loggedOnce > 0 && f.cameBack === 0 ? 'destructive' : 'default'}
        />
        <StatCard label="Signups · 7d" value={k.signups7d} icon={UserPlus} />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FunnelCard funnel={f} />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Daily logs · 30d</CardTitle>
            <CardDescription>Unique users who tapped done or missed each day.</CardDescription>
          </CardHeader>
          <CardContent>
            <TimeseriesChart
              series={[
                {
                  key: 'done',
                  label: 'Done',
                  color: 'hsl(var(--success))',
                  values: d.dailyLoggers.map((p) => ({ ts: p.date, value: p.done })),
                },
                {
                  key: 'missed',
                  label: 'Missed',
                  color: 'hsl(var(--warning))',
                  values: d.dailyLoggers.map((p) => ({ ts: p.date, value: p.missed })),
                },
              ]}
              height={200}
              yFormatter={(v) => String(Math.round(v))}
            />
          </CardContent>
        </Card>
      </section>

      <CohortsCard cohorts={d.cohorts} />

      <LiveExperimentsCard rows={d.liveExperiments} />

      <AwaitingWrapCard rows={d.awaitingWrap} stale={d.staleEvaluating} />
    </div>
  );
}

function ReminderLine({ reminder }: { reminder: PivotData['reminder'] }) {
  if (!reminder.allowed)
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
        <BellOff className="h-4 w-4 text-destructive" aria-hidden />
        <span>
          The daily reminder is <strong>blocked</strong>: <code>experiment_daily_reminder</code> is not in the
          notification allowlist.
        </span>
      </div>
    );
  return (
    <div className="flex items-center gap-2 rounded-lg border px-4 py-3 text-sm text-muted-foreground">
      <Bell className="h-4 w-4" aria-hidden />
      <span>
        Daily reminder is on: <span className="font-medium text-foreground">{reminder.sent7d}</span> sent to{' '}
        <span className="font-medium text-foreground">{reminder.users7d}</span> users in the last 7 days. Opens are
        not tracked.
      </span>
    </div>
  );
}

function FunnelCard({ funnel }: { funnel: FunnelCounts }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Funnel since pivot</CardTitle>
        <CardDescription>Each user counted once. The loop works when people come back after day 1.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {FUNNEL_STEPS.map((s, i) => {
          const value = funnel[s.key];
          const prev = i > 0 ? funnel[FUNNEL_STEPS[i - 1].key] : value;
          return (
            <div key={s.key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span>{s.label}</span>
                <span className="tabular-nums">
                  <span className="font-semibold">{value}</span>
                  {i > 0 && <span className="ml-2 text-xs text-muted-foreground">{pct(value, prev)}% of prev</span>}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary" style={{ width: `${pct(value, funnel.signedUp)}%` }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function CohortsCard({ cohorts }: { cohorts: PivotData['cohorts'] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Weekly signup groups</CardTitle>
        <CardDescription>Users grouped by the week they signed up. Newer weeks are still in progress.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {cohorts.length === 0 ? (
          <EmptyRow text="No signups since the pivot." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week of</TableHead>
                  {FUNNEL_STEPS.map((s) => (
                    <TableHead key={s.key} className="text-right">
                      {s.short}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts.map((c) => (
                  <TableRow key={c.weekStart}>
                    <TableCell className="whitespace-nowrap">{formatDay(c.weekStart)}</TableCell>
                    {FUNNEL_STEPS.map((s) => (
                      <TableCell key={s.key} className="text-right tabular-nums">
                        {c[s.key]}
                        {s.key !== 'signedUp' && c.signedUp > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">{pct(c[s.key], c.signedUp)}%</span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UserLink({ userId, displayName, email }: { userId: string; displayName?: string; email?: string }) {
  return (
    <Link to={`/users/${encodeURIComponent(userId)}`} className="font-medium hover:underline">
      {displayName ?? email ?? userId}
    </Link>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <p className="px-4 py-6 text-center text-sm text-muted-foreground">{text}</p>;
}

function LiveExperimentsCard({ rows }: { rows: PivotData['liveExperiments'] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Live experiments</CardTitle>
        <CardDescription>Riskiest first. Silent = no log for 4+ days, or none since day 1.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <EmptyRow text="No live experiments." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Experiment</TableHead>
                  <TableHead className="text-right">Day</TableHead>
                  <TableHead className="text-right">Days logged</TableHead>
                  <TableHead>Last log</TableHead>
                  <TableHead>Health</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.phaseId}>
                    <TableCell className="whitespace-nowrap">
                      <UserLink userId={r.userId} displayName={r.displayName} email={r.email} />
                    </TableCell>
                    <TableCell className="max-w-[320px]">
                      <div className="truncate" title={r.objective}>
                        {r.objective}
                      </div>
                      {r.phaseNumber > 1 && (
                        <div className="text-xs text-muted-foreground">Experiment #{r.phaseNumber}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.day}/{r.totalDays}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.daysLogged}
                      {r.loggedToday && (
                        <CheckCircle2
                          className="ml-1 inline h-3.5 w-3.5 text-[hsl(var(--success))]"
                          aria-label="logged today"
                        />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatRelative(r.lastLogAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={HEALTH[r.health].variant}>{HEALTH[r.health].label}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AwaitingWrapCard({ rows, stale }: { rows: PivotData['awaitingWrap']; stale: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Ended, waiting for the user</CardTitle>
        <CardDescription>
          Experiments that ended in the last 14 days and the user has not wrapped up.
          {stale > 0 && ` ${stale} older ones are treated as abandoned and not listed.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <EmptyRow text="Nobody waiting." />
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.phaseId} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">
                    <UserLink userId={r.userId} displayName={r.displayName} email={r.email} />
                  </div>
                  <div className="truncate text-xs text-muted-foreground" title={r.objective}>
                    {r.objective} · logged {r.daysLogged} of 7 days
                  </div>
                </div>
                <Badge variant={r.daysWaiting >= 3 ? 'destructive' : 'outline'}>
                  {r.daysWaiting === 0 ? 'ended today' : `${r.daysWaiting}d ago`}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
